/**
 * Topnav Identity Indicator
 * Shows monitor identity and WebSocket session status in the header
 * Compact display with tooltip for detailed information
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useWebRTC } from '../../context/WebRTCContext';
import { getSession } from '../../services/auth';
import './Topnav.Identity.css';

const TopnavIdentity = () => {
  const { monitorId, isConnected, status, wsUrl, sessionId } = useWebRTC();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [userId, setUserId] = useState(null);
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  // Fetch userId from Cognito session
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const session = await getSession();
        if (session?.userId) {
          setUserId(session.userId);
        }
      } catch (err) {
        console.error('Failed to get userId:', err);
      }
    };
    fetchUserId();
  }, []);

  // Get shortened IDs for display
  const shortMonitorId = monitorId ? monitorId.substring(0, 16) : '—';

  // Determine connection state
  const getConnectionState = () => {
    if (!wsUrl) return 'unconfigured';
    if (isConnected) return 'connected';
    if (status?.includes('Reconnecting')) return 'reconnecting';
    return 'disconnected';
  };

  const connectionState = getConnectionState();

  // Position tooltip below the icon
  useEffect(() => {
    if (showTooltip && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.right - 380, // Right-align tooltip
      });
    }
  }, [showTooltip]);

  // Handle click outside to close tooltip
  const handleClickOutside = useCallback((event) => {
    // If clicking on the icon button, let the toggle handler manage it
    if (iconRef.current && iconRef.current.contains(event.target)) {
      return;
    }
    // If clicking inside the tooltip, don't close it
    if (tooltipRef.current && tooltipRef.current.contains(event.target)) {
      return;
    }
    // Click was outside both icon and tooltip - close it
    setShowTooltip(false);
  }, []);

  useEffect(() => {
    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip, handleClickOutside]);

  return (
    <div className="identity-indicator">
      <button
        ref={iconRef}
        className={`identity-icon-btn ${connectionState}`}
        onClick={() => setShowTooltip(!showTooltip)}
        data-tooltip="Monitor Identity & Session"
        aria-label="Show identity information"
      >
        {/* Fingerprint/ID icon */}
        <svg
          className="identity-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 10a2 2 0 0 1 2 2c0 1.02-.1 2.51-.26 4" />
          <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
          <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
          <path d="M2 12a10 10 0 0 1 18-6" />
          <path d="M2 16h.01" />
          <path d="M21.8 16c.2-2 .131-5.354 0-6" />
          <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
          <path d="M8.65 22c.21-.66.45-1.32.57-2" />
          <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2" />
        </svg>
        {/* Status dot */}
        <span className={`status-dot ${connectionState}`} />
      </button>

      {/* Tooltip panel */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="identity-tooltip"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="identity-tooltip-header">
            <span className="tooltip-title">Monitor Identity</span>
            <span className={`connection-badge ${connectionState}`}>
              {connectionState === 'connected' && 'Online'}
              {connectionState === 'reconnecting' && 'Reconnecting'}
              {connectionState === 'disconnected' && 'Offline'}
              {connectionState === 'unconfigured' && 'Not Configured'}
            </span>
          </div>

          <div className="identity-tooltip-body">
            {/* User ID (Cognito sub - used for pairing) */}
            {userId && (
              <div className="identity-row">
                <span className="identity-label">USER ID</span>
                <span className="identity-value user-id">{userId}</span>
              </div>
            )}

            {/* Monitor ID (browser-specific, legacy) */}
            <div className="identity-row">
              <span className="identity-label">BROWSER</span>
              <span className="identity-value identity">{shortMonitorId}</span>
            </div>

            {/* WebSocket Status */}
            <div className="identity-row">
              <span className="identity-label">SESSION</span>
              <span className={`identity-value session ${connectionState}`}>
                {sessionId || '—'}
              </span>
            </div>

            {/* WebSocket URL (if configured) */}
            {wsUrl && (
              <div className="identity-row url-row">
                <span className="identity-label">SERVER</span>
                <span className="identity-value url" data-tooltip={wsUrl}>
                  {wsUrl.replace(/^wss?:\/\//, '').substring(0, 28)}...
                </span>
              </div>
            )}
          </div>

          <div className="identity-tooltip-footer">
            <span className="footer-hint">
              User ID is used for pairing (works across browsers)
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopnavIdentity;
